import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, PrivateRoute, SessionTimeoutProvider } from '@/features/auth';
import { ThemeProvider } from '@/features/theme/contexts/ThemeContext';
import { RefreshProvider } from '@/contexts/RefreshContext';
import { DateRangeProvider } from '@/contexts/DateRangeContext';
import { TimezoneProvider } from '@/contexts/TimezoneContext';
import { FilterProvider } from '@/contexts/FilterContext';
import { MainLayout } from '@/layouts/MainLayout';
import { LoginPage } from '@/pages/LoginPage';
import { PageLoader } from '@/components/ui/page-loader';
import { queryClient } from '@/lib/query-client';
import { Toaster } from '@/components/ui/toaster';
import { clearAuthTokens } from '@/utils/clear-auth';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Lazy load heavy pages
const HomePage = lazy(() => import('@/pages/HomePage').then(module => ({ default: module.HomePage })));
const TrackerPage = lazy(() => import('@/pages/TrackerPage').then(module => ({ default: module.TrackerPage })));
const SalesPage = lazy(() => import('@/pages/SalesPage').then(module => ({ default: module.SalesPage })));
const RefundsReportPage = lazy(() => import('@/pages/RefundsReportPage').then(module => ({ default: module.RefundsReportPage })));
const DREPage = lazy(() => import('@/pages/DREPage').then(module => ({ default: module.DREPage })));
const TeamPage = lazy(() => import('@/pages/TeamPage').then(module => ({ default: module.TeamPage })));
const CreativesAnalysisPageV2 = lazy(() => import('@/pages/CreativesAnalysisPageV2').then(module => ({ default: module.CreativesAnalysisPageV2 })));
const CampaignsPage = lazy(() => import('@/pages/CampaignsPage').then(module => ({ default: module.CampaignsPage })));
const LandersPage = lazy(() => import('@/pages/LandersPage').then(module => ({ default: module.LandersPage })));
const CashFlowPage = lazy(() => import('@/pages/CashFlowPage').then(module => ({ default: module.CashFlowPage })));
const ProfitDistributionPage = lazy(() => import('@/pages/ProfitDistributionPage').then(module => ({ default: module.ProfitDistributionPage })));
const SectorDetailPage = lazy(() => import('@/pages/SectorDetailPage').then(module => ({ default: module.SectorDetailPage })));
const CollaboratorDetailPage = lazy(() => import('@/pages/CollaboratorDetailPage').then(module => ({ default: module.CollaboratorDetailPage })));

const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(module => ({ default: module.SettingsPage })));
const ChangelogPage = lazy(() => import('@/pages/ChangelogPage').then(module => ({ default: module.ChangelogPage })));
const OrderAuditPage = lazy(() => import('@/pages/OrderAuditPage').then(module => ({ default: module.OrderAuditPage })));
const AuditoriaPage = lazy(() => import('@/pages/AuditoriaPage').then(module => ({ default: module.AuditoriaPage })));
const ProfilePage = lazy(() => import('@/pages/ProfilePage').then(module => ({ default: module.ProfilePage })));

function App() {
  // Handle auth errors (invalid tokens)
  useEffect(() => {
    // Listen for unhandled promise rejections that might be auth errors
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      if (error?.message?.includes('Invalid Refresh Token') ||
          error?.message?.includes('Refresh Token Not Found')) {
        console.log('Detected invalid refresh token, clearing auth...');
        clearAuthTokens();
        event.preventDefault(); // Prevent error from showing in console
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Also make clearAuthTokens available globally for manual use
    (window as unknown as { clearAuthTokens: typeof clearAuthTokens }).clearAuthTokens = clearAuthTokens;

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <Router>
          <ThemeProvider defaultTheme="dark">
            <AuthProvider>
              <TimezoneProvider>
                <DateRangeProvider>
                  <FilterProvider>
                    <RefreshProvider>
                    <SessionTimeoutProvider timeoutMinutes={30} warningMinutes={5}>
                <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />

                {/* Private routes */}
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <MainLayout />
                    </PrivateRoute>
                  }
                >
                  <Route 
                    index 
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <HomePage />
                      </Suspense>
                    } 
                  />
                  <Route
                    path="tracker"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <TrackerPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="creativesv2"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <CreativesAnalysisPageV2 />
                      </Suspense>
                    }
                  />
                  <Route
                    path="campanhas"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <CampaignsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="landers"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <LandersPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="sales"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <SalesPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="distribuicao-lucros"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <ProfitDistributionPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="distribuicao-lucros/:setorId"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <SectorDetailPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="distribuicao-lucros/:setorId/:colaboradorId"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <CollaboratorDetailPage />
                      </Suspense>
                    }
                  />
                  {/* Redirect antigo para novo path */}
                  <Route
                    path="profit-distribution"
                    element={<Navigate to="/distribuicao-lucros" replace />}
                  />
                  <Route
                    path="reports/refunds"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <RefundsReportPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="reports/dre"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <DREPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="team"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <TeamPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="financeiro"
                    element={
                      <PrivateRoute requiredRole={['admin', 'head']}>
                        <Suspense fallback={<PageLoader />}>
                          <CashFlowPage />
                        </Suspense>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <PrivateRoute requiredRole={['admin', 'head']}>
                        <Suspense fallback={<PageLoader />}>
                          <SettingsPage />
                        </Suspense>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="changelog"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <ChangelogPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="auditoria"
                    element={
                      <PrivateRoute requiredRole={['admin']}>
                        <Suspense fallback={<PageLoader />}>
                          <AuditoriaPage />
                        </Suspense>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/postback-errors"
                    element={
                      <PrivateRoute requiredRole={['admin', 'head']}>
                        <Suspense fallback={<PageLoader />}>
                          <OrderAuditPage />
                        </Suspense>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="profile"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <ProfilePage />
                      </Suspense>
                    }
                  />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
                    </SessionTimeoutProvider>
                  </RefreshProvider>
                  </FilterProvider>
                </DateRangeProvider>
              </TimezoneProvider>
            </AuthProvider>
          </ThemeProvider>
        </Router>
      </ErrorBoundary>
      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;